import React, { useRef, useState, useEffect } from "react";
import {
  Space,
  Divider,
  Typography,
  Avatar,
  Badge,
  Button,
  Tooltip,
  theme,
  Tag,
} from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  BankOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  LockOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ITicket,
  ITicketThreadItem,
  ITicketActivity,
  TicketPosterType,
} from "../../../Services/TicketServices";
import { useUser } from "../../../Context/UserContext";
import { getFileIcon } from "./utils";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const getStatusColorStr = (statusStr: string) => {
  const map: Record<string, string> = {
    "open": "processing",
    "in progress": "purple",
    "in-progress": "purple",
    "under research": "cyan",
    "in testing": "warning",
    "testing": "warning",
    "ready for release": "success",
    "closed": "default"
  };
  return map[statusStr.toLowerCase()] || "default";
};

const formatSystemMessage = (body: string, ticket?: ITicket, activity?: ITicketActivity[]): React.ReactNode => {
  if (activity && activity.length > 0) {
    return (
      <Space size={8} direction="vertical" style={{ width: "100%" }}>
        {activity.map((act, i) => {
          const fieldName = act.fieldName || (act as any).FieldName || "Activity";
          const fromVal = act.from || (act as any).From || "-";
          const toVal = act.to || (act as any).To || "-";

          let fromColor = "volcano";
          let toColor = "green";
          if (fieldName.toLowerCase().includes("status")) {
            fromColor = getStatusColorStr(fromVal);
            toColor = getStatusColorStr(toVal);
          }

          return (
            <Space key={i} size={8} align="center" style={{ flexWrap: "wrap", fontSize: 12 }}>
              <Text strong style={{ textTransform: "capitalize", minWidth: 70 }}>
                {fieldName}:
              </Text>
              <Tag color={fromColor} style={{ margin: 0, borderRadius: 4, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
                <span style={{ opacity: 0.75, marginRight: 4, fontWeight: 400 }}>from</span>
                {fromVal}
              </Tag>
              <span style={{ color: "#1890ff", fontWeight: "bold", fontSize: 14 }}>&rarr;</span>
              <Tag color={toColor} style={{ margin: 0, borderRadius: 4, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
                <span style={{ opacity: 0.75, marginRight: 4, fontWeight: 400 }}>to</span>
                {toVal}
              </Tag>
            </Space>
          );
        })}
      </Space>
    );
  }

  // Strip HTML paragraph tags like <p> from raw DB body and clean rnrn / rn newline artifacts
  const sanitizedBody = (body || "")
    .replace(/rnrn/g, "\n\n")
    .replace(/\r\n/g, "\n")
    .replace(/rn/g, "\n");

  const cleanBody = sanitizedBody.replace(/<\/?[^>]+(>|$)/g, "").trim();

  let parsedReason: string | null = null;
  let processableBody = cleanBody;
  const reasonMatch = cleanBody.match(/\s*Reason:\s*(.*)/i);
  if (reasonMatch) {
    parsedReason = reasonMatch[1].trim();
    processableBody = cleanBody.replace(/\s*Reason:\s*(.*)/i, "").trim();
  }

  const renderWithReason = (content: React.ReactNode) => {
    if (!parsedReason) return content;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div>{content}</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>
          <span style={{ fontWeight: 600 }}>Reason:</span> {parsedReason}
        </div>
      </div>
    );
  };

  // 1. Status Changes
  const statusMatch = processableBody.match(/status changed from ([\w\s-]+) to ([\w\s-]+)/i);
  if (statusMatch) {
    const fromStatus = statusMatch[1].trim();
    const toStatus = statusMatch[2].trim();

    return renderWithReason(
      <Space size={6} align="center" style={{ flexWrap: "wrap" }}>
        <span>Ticket status changed:</span>
        <Tag color={getStatusColorStr(fromStatus)} style={{ margin: 0, borderRadius: 4, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
          <span style={{ opacity: 0.75, marginRight: 4, fontWeight: 400 }}>from</span>
          {fromStatus}
        </Tag>
        <span style={{ color: "#1890ff", fontWeight: "bold", fontSize: 14 }}>&rarr;</span>
        <Tag color={getStatusColorStr(toStatus)} style={{ margin: 0, borderRadius: 4, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
          <span style={{ opacity: 0.75, marginRight: 4, fontWeight: 400 }}>to</span>
          {toStatus}
        </Tag>
      </Space>
    );
  }

  // 2. Escalations
  if (processableBody.toLowerCase().includes("escalated due to")) {
    const parts = processableBody.split(/due to:?/i);
    const reason = parts[1] ? parts[1].trim() : "";
    return renderWithReason(
      <Space size={4} style={{ flexWrap: "wrap" }}>
        <span>Ticket has been</span>
        <Tag color="error" style={{ margin: 0, borderRadius: 4, padding: "0 6px", fontSize: 11 }}>Escalated</Tag>
        {reason && (
          <>
            <span>due to:</span>
            <span style={{ fontStyle: "italic", opacity: 0.85, fontSize: "12px" }}>"{reason}"</span>
          </>
        )}
      </Space>
    );
  }

  const renderDependencyTags = (prefixText: string, fullBodyText: string, splitRegex: RegExp) => {
    const numbers = fullBodyText.match(/#\d+/g) || [];
    const content = (
      <Space size={6} style={{ flexWrap: "wrap" }}>
        <span>{prefixText}:</span>
        {numbers.map((num, i) => {
          const cleanNum = num.replace("#", "");
          const dep = ticket?.dependencies?.find(d =>
            d.name === num || d.name === cleanNum || (d as any).ticketRef === num || (d as any).ticketRef === cleanNum || d.id === cleanNum
          );
          const isCurrentTicket = ticket?.number === num || ticket?.number === cleanNum;
          const targetId = isCurrentTicket ? ticket?.id : dep?.id;

          if (targetId) {
            return (
              <a
                key={i}
                href={`/tickets/${targetId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-underline-tag"
                style={{ textDecoration: 'none' }}
              >
                <Tag
                  color="cyan"
                  style={{
                    margin: 0,
                    borderRadius: 4,
                    padding: "0 6px",
                    fontSize: 11,
                    fontWeight: "bold",
                    cursor: 'pointer'
                  }}
                >
                  {num}
                </Tag>
              </a>
            );
          }
          return (
            <Tag key={i} color="cyan" style={{ margin: 0, borderRadius: 4, padding: "0 6px", fontSize: 11, fontWeight: "bold" }}>
              {num}
            </Tag>
          );
        })}
        {numbers.length === 0 && <span>{fullBodyText.split(splitRegex)[1]}</span>}
      </Space>
    );
    return renderWithReason(content);
  };

  if (processableBody.toLowerCase().includes("unmerged tickets")) {
    return renderDependencyTags("Unmerged tickets", processableBody, /unmerged tickets:?/i);
  }

  // 3. Merges (Outgoing)
  if (processableBody.toLowerCase().includes("merged tickets")) {
    return renderDependencyTags("Merged tickets", processableBody, /merged tickets:?/i);
  }

  // 3.5. Merged (Incoming)
  if (processableBody.toLowerCase().includes("merged into ticket")) {
    return renderDependencyTags("Merged into ticket", processableBody, /merged into ticket:?/i);
  }

  if (processableBody.toLowerCase().includes("unlinked tickets")) {
    return renderDependencyTags("Unlinked tickets", processableBody, /unlinked tickets:?/i);
  }

  // 3.6. Linked (Outgoing / List)
  if (processableBody.toLowerCase().includes("linked tickets")) {
    return renderDependencyTags("Linked tickets", processableBody, /linked tickets:?/i);
  }

  // 3.7. Linked (Incoming)
  if (processableBody.toLowerCase().includes("linked to ticket")) {
    return renderDependencyTags("Linked to ticket", processableBody, /linked to ticket:?/i);
  }

  // 4. Client Input Required
  if (processableBody.toLowerCase().includes("requested additional input")) {
    return renderWithReason(
      <Space size={4} style={{ flexWrap: "wrap" }}>
        <span>Practice staff</span>
        <Tag color="warning" style={{ margin: 0, borderRadius: 4, padding: "0 6px", fontSize: 11 }}>Requested Input</Tag>
        <span>from the client.</span>
      </Space>
    );
  }

  // Fallback: render normally
  return <span dangerouslySetInnerHTML={{ __html: sanitizedBody.replace(/\n/g, "<br/>") }} />;
};

interface TicketThreadProps {
  ticket: ITicket;
  onDownloadAttachment: (file: any) => Promise<void>;
  downloading: boolean;
  threadEndRef: React.RefObject<HTMLDivElement>;
  onRefreshThread?: () => Promise<void> | void;
  refreshingThread?: boolean;
}

export const TicketThread: React.FC<TicketThreadProps> = ({
  ticket,
  onDownloadAttachment,
  downloading,
  threadEndRef,
  onRefreshThread,
  refreshingThread,
}) => {
  const { token } = theme.useToken();
  const { getUserId } = useUser();
  const isDark = token.colorTextBase === "#E9EEF9";
  const subtleBorder = `1px solid ${token.colorBorderSecondary}`;

  const threadStartRef = useRef<HTMLDivElement>(null);
  const [isTopVisible, setIsTopVisible] = useState(true);
  const [isBottomVisible, setIsBottomVisible] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === threadStartRef.current) {
            setIsTopVisible(entry.isIntersecting);
          } else if (entry.target === threadEndRef.current) {
            setIsBottomVisible(entry.isIntersecting);
          }
        });
      },
      { threshold: 0 }
    );

    const topNode = threadStartRef.current;
    const bottomNode = threadEndRef.current;

    if (topNode) observer.observe(topNode);
    if (bottomNode) observer.observe(bottomNode);

    return () => {
      if (topNode) observer.unobserve(topNode);
      if (bottomNode) observer.unobserve(bottomNode);
    };
  }, [ticket?.threads]);

  const scrollToThreadTop = () => {
    threadStartRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToThreadBottom = () => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="ticket-thread-container" style={{ display: "flex", flexDirection: "column" }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .hover-underline-tag:hover .ant-tag {
          text-decoration: underline !important;
        }
      `}} />
      {/* 1. Ticket Core Description Section */}
      <div style={{ padding: "24px 24px 20px 24px" }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Title level={3} style={{ margin: "0 0 10px 0", fontWeight: 600, fontSize: 20, color: token.colorTextHeading }}>
              {ticket.title}
            </Title>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
              <Space size="small">
                <Avatar
                  size={24}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimary }}
                />
                <Text strong style={{ fontSize: 13, color: token.colorText }}>
                  {ticket.createdBy?.userName}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  opened {dayjs(ticket.createdBy?.date).fromNow()}
                </Text>
              </Space>

              <Divider type="vertical" style={{ margin: 0 }} />

              <Space size={4} style={{ fontSize: 12, color: token.colorTextDescription }}>
                <ClockCircleOutlined />
                <span>{dayjs(ticket.createdBy?.date).format("DD MMM YYYY, hh:mm A")}</span>
              </Space>

              {ticket.company?.name && (
                <>
                  <Divider type="vertical" style={{ margin: 0 }} />
                  <Space size={4} style={{ fontSize: 12, color: token.colorTextDescription }}>
                    <BankOutlined />
                    <span>{ticket.company.name}</span>
                  </Space>
                </>
              )}
            </div>
          </div>

          {/* Core Message Body HTML */}
          <div
            className="ticket-description-box"
            style={{
              padding: "16px 20px",
              borderRadius: 8,
              border: subtleBorder,
              background: token.colorBgLayout,
              fontSize: 13.5,
              lineHeight: 1.6,
              color: token.colorText,
              maxHeight: "350px",
              overflowY: "auto",
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: ticket.message }} />
          </div>

          {/* Ticket Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ fontSize: 12, display: "block", marginBottom: 8, color: token.colorTextDescription }}>
                Ticket Attachments ({ticket.attachments.length})
              </Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ticket.attachments.map((file, idx) => (
                  <div
                    key={file.id || idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                      borderRadius: 6,
                      background: token.colorBgContainer,
                      border: subtleBorder,
                      fontSize: 12,
                    }}
                  >
                    {getFileIcon(file.name)}
                    <span
                      style={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: token.colorText,
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <Tooltip title="Download file">
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled={downloading}
                        onClick={() => onDownloadAttachment(file)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 0, width: 22, height: 22 }}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Space>
      </div>

      <Divider style={{ margin: 0 }} />

      {/* 2. Conversation Feed Activity Thread */}
      <div style={{ padding: "24px 24px" }}>
        <div ref={threadStartRef} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MessageOutlined style={{ color: token.colorPrimary }} />
            <Text strong style={{ fontSize: 13.5, color: token.colorTextHeading }}>
              Conversation activity thread
            </Text>
            <Badge
              count={ticket?.threads?.length || 0}
              style={{
                backgroundColor: token.colorFillAlter,
                color: token.colorTextSecondary,
                boxShadow: "none",
                fontWeight: 600,
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MessageOutlined style={{ color: token.colorPrimary }} />
            <Text strong style={{ fontSize: 13.5, color: token.colorTextHeading }}>
              Conversation activity thread
            </Text>
            <Badge
              count={ticket?.threads?.length || 0}
              style={{
                backgroundColor: token.colorFillAlter,
                color: token.colorTextSecondary,
                boxShadow: "none",
                fontWeight: 600,
              }}
            />
          </div>
          {onRefreshThread && (
            <Tooltip title="Refresh conversation thread">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined spin={refreshingThread} />}
                loading={refreshingThread}
                onClick={onRefreshThread}
                style={{ borderRadius: 6, display: "flex", alignItems: "center", gap: 4, color: token.colorTextSecondary }}
              >
                Refresh
              </Button>
            </Tooltip>
          )}
        </div>

        {ticket?.threads && ticket?.threads.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {ticket?.threads.map((item, index) => {
              // A. Timeline system log messages (full width greyed out box with yellow icon)
              if (item.isSystem) {
                return (
                  <div
                    key={item.id || index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: token.colorTextSecondary,
                        marginBottom: "4px",
                      }}
                    >
                      {`${item.createdBy?.userName ? item.createdBy?.userName + " on " : ""} ${dayjs(item.createdBy?.date).format("DD/MM/YYYY hh:mm A")}`}
                      {item.ticketRef && (
                        <Tag color="blue" bordered={false} style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                          Merged from #{item.ticketRef}
                        </Tag>
                      )}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px 14px",
                        width: "100%",
                        backgroundColor: token.colorFillAlter,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: "6px",
                        fontSize: "12.5px",
                        color: token.colorTextHeading,
                      }}
                    >
                      <InfoCircleOutlined style={{ fontSize: 14, color: token.colorWarning }} />
                      {formatSystemMessage(item.body, ticket, item.activity || (item as any).Activity)}
                    </div>
                  </div>
                );
              }

              const isOwn = item.createdBy?.userId === getUserId();

              // B. Private Internal Note (special styling using warning tokens)
              if (item.isPrivate) {
                return (
                  <div
                    key={item.id || index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isOwn ? "flex-end" : "flex-start",
                      width: "100%",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: token.colorTextSecondary,
                        marginBottom: "4px",
                        textAlign: isOwn ? "right" : "left",
                      }}
                    >
                      {`${isOwn ? "You" : item.createdBy?.userName}`} (Note) on {dayjs(item.createdBy?.date).format("DD/MM/YYYY hh:mm A")}
                      {item.ticketRef && (
                        <Tag color="blue" bordered={false} style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                          Merged from {item.ticketRef}
                        </Tag>
                      )}
                    </span>
                    <div
                      style={{
                        maxWidth: "75%",
                        padding: "10px 14px",
                        borderRadius: isOwn ? "6px 6px 2px 6px" : "6px 6px 6px 2px",
                        backgroundColor: token.colorWarningBg,
                        border: `1px dashed ${token.colorWarningBorder}`,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                        wordBreak: "break-word",
                        fontSize: "12.5px",
                        color: token.colorText,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontWeight: 600, color: token.colorWarningActive }}>
                        <LockOutlined style={{ fontSize: 12 }} />
                        <span style={{ fontSize: 11 }}>Note</span>
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: item.body }} />

                      {/* Thread item attachments */}
                      {((item.attachments && item.attachments.length > 0) || ((item as any).Attachments && (item as any).Attachments.length > 0)) && (
                        <div style={{ marginTop: 8, borderTop: "1px dashed rgba(0,0,0,0.1)", paddingTop: 6 }}>
                          {((item.attachments || (item as any).Attachments) as any[]).map((file: any, fidx: number) => {
                            const fileName = file?.name || file?.Name || file?.path || file?.Path || (typeof file === "string" ? file : "Attachment");
                            return (
                              <div key={file.id || file.Id || fidx} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 4 }}>
                                {getFileIcon(fileName)}
                                <span style={{ fontWeight: 500 }} >
                                  {fileName}
                                </span>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DownloadOutlined style={{ fontSize: 12 }} />}
                                  disabled={downloading}
                                  onClick={() => onDownloadAttachment(file)}
                                  style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 0, width: 22, height: 22 }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              // C. Public Messages (Right Aligned for "You" [info tokens], Left Aligned for others [fillAlter tokens])
              return (
                <div
                  key={item.id || index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isOwn ? "flex-end" : "flex-start",
                    width: "100%",
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      color: token.colorTextSecondary,
                      marginBottom: "4px",
                      textAlign: isOwn ? "right" : "left",
                    }}
                  >
                    {`${isOwn ? "You" : item.createdBy?.userName} on ${dayjs(item.createdBy?.date).format("DD/MM/YYYY hh:mm A")}`}
                    {item.ticketRef && (
                      <Tag color="blue" bordered={false} style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                        Merged from {item.ticketRef}
                      </Tag>
                    )}
                  </span>
                  <div
                    style={{
                      maxWidth: "75%",
                      padding: "10px 14px",
                      borderRadius: isOwn ? "6px 6px 2px 6px" : "6px 6px 6px 2px",
                      backgroundColor: isOwn ? token.colorInfoBg : token.colorFillAlter,
                      border: `1px solid ${isOwn ? token.colorInfoBorder : token.colorBorderSecondary}`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      wordBreak: "break-word",
                      fontSize: "12.5px",
                      color: token.colorText,
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: item.body }} />

                    {/* Thread item attachments */}
                    {((item.attachments && item.attachments.length > 0) || ((item as any).Attachments && (item as any).Attachments.length > 0)) && (
                      <div style={!item.body ? {} : { marginTop: 8, borderTop: "1px dashed rgba(0,0,0,0.1)", paddingTop: 6 }}>
                        {((item.attachments || (item as any).Attachments) as any[]).map((file: any, fidx: number) => {
                          const fileName = file?.name || file?.Name || file?.path || file?.Path || (typeof file === "string" ? file : "Attachment");
                          return (
                            <div key={file.id || file.Id || fidx} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 4 }}>
                              {getFileIcon(fileName)}
                              <span style={{ fontWeight: 500 }}>
                                {fileName}
                              </span>
                              <Button
                                type="text"
                                size="small"
                                icon={<DownloadOutlined style={{ fontSize: 12 }} />}
                                disabled={downloading}
                                onClick={() => onDownloadAttachment(file)}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 0, width: 22, height: 22 }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={threadEndRef} />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <MessageOutlined style={{ fontSize: 24, color: token.colorTextPlaceholder, marginBottom: 8 }} />
            <p style={{ margin: 0, color: token.colorTextDescription, fontSize: 12 }}>
              No activity logs or messages in this discussion yet.
            </p>
          </div>
        )}
      </div>

      {/* Sticky floating buttons anchored to the thread column */}
      <div
        style={{
          position: "sticky",
          bottom: 24,
          height: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 100
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {!isTopVisible && (
            <Tooltip title="Scroll to top">
              <Button
                shape="circle"
                size="middle"
                icon={<ArrowUpOutlined />}
                onClick={scrollToThreadTop}
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: subtleBorder }}
              />
            </Tooltip>
          )}
          {!isBottomVisible && (
            <Tooltip title="Go to newest">
              <Button
                shape="round"
                size="middle"
                icon={<ArrowDownOutlined />}
                onClick={scrollToThreadBottom}
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: subtleBorder, minWidth:32, padding: 0}}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
